<template>
  <v-app-bar
    class="header !tw-z-20"
    :class="{ 'is-intro': isShowIntro }"
    app
    elevation="0"
    dense
    color="#5656B2"
  >
    <v-btn
      v-if="!isDesktop && $route.matched.some((r) => r.path === '/panel')"
      icon
      class="hamburger"
      :class="{ opened: isOpenNav }"
      @click="$emit('change-nav')"
    >
      <v-icon size="26" color="white" dark>{{
        isOpenNav ? 'mdi-backburger' : 'mdi-forwardburger'
      }}</v-icon>
    </v-btn>
    <div v-if="logo" class="logo">
      <Logo height="120px" width="32px" />
    </div>
    <div v-if="showSequoiaLogo" class="sequoia-logo">
      <img :src="SequoiaLogo" alt="" />
    </div>

    <v-spacer></v-spacer>

    <SearchBox v-if="showSearchBox" :disabled="isCurrentGolive" />

    <div v-if="isShowIntro" class="profile-position">
      <FlashingButton
        :contents="calendarContents"
        title="Calendar Preferences"
        content-class="profile-position"
        right
        @close="$emit('closeInfo')"
        @click="showCalendar"
      />
    </div>
    <v-menu
      v-model="profile"
      bottom
      left
      offset-y
      nudge-bottom="5px"
      close-on-content-click
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn dark icon v-bind="attrs" class="mr-2" v-on="on">
          <v-avatar color="white" ripple size="42">
            <span
              v-if="!profilePicture"
              class="text-0d75 font-weight-medium avatar"
              >{{ initials }}</span
            >
            <img v-if="profilePicture" :src="profilePicture" alt="profile" />
          </v-avatar>
        </v-btn>
      </template>
      <div class="tw-bg-white tw-shadow-shadow-2 tw-py-2 tw-w-[232px]">
        <div
          class="tw-px-4 tw-py-2 hover:tw-bg-highlight tw-cursor-pointer"
          @click="$router.push('/ats-settings')"
        >
          <Typography variant="body-2"> ATS Connection Setup </Typography>
        </div>
        <v-tooltip left max-width="250px" :disabled="!calendarDisabled">
          <template v-slot:activator="{ on, attrs }">
            <div
              class="tw-px-4 tw-py-2 hover:tw-bg-highlight tw-cursor-pointer"
              :class="{ 'tw-opacity-30': calendarDisabled }"
              v-bind="attrs"
              v-on="on"
              @click="openCalendar()"
            >
              <Typography variant="body-2"> Calendar Preferences </Typography>
            </div>
          </template>
          <span>
            We are still syncing your calendar. Please check back shortly!
          </span>
        </v-tooltip>
        <div
          class="tw-px-4 tw-py-2 hover:tw-bg-highlight tw-cursor-pointer"
          @click="$emit('add-read-scopes')"
        >
          <Typography variant="body-2"> Additional Permissions </Typography>
        </div>
        <div
          v-if="showSwitchToV2"
          class="tw-px-4 tw-py-2 hover:tw-bg-highlight tw-cursor-pointer"
          @click="onRedirectToV2"
        >
          <Typography variant="body-2">
            Switch to Navigator/FastTrack
          </Typography>
        </div>
        <div
          class="tw-px-4 tw-py-2 hover:tw-bg-highlight tw-cursor-pointer"
          @click="signOut"
        >
          <Typography variant="body-2"> Sign out </Typography>
        </div>
      </div>
    </v-menu>

    <template v-if="$slots.extension" v-slot:extension>
      <slot name="extension"></slot>
    </template>
    <div v-if="isShowIntro" class="overlay"></div>
  </v-app-bar>
</template>

<script>
import axios from 'axios';
import Cookies from 'js-cookie';
import * as Sentry from '@sentry/vue';
import consola from 'consola';
import { post } from '@/js/api/sourcer';
import Config from '@/js/config';
import Constants from '@/js/constants';
import SearchBox from '@/components/searchBox/';
import CloseIcon from '@/assets/img/close-svgrepo-com.svg';
import SequoiaLogo from '@/assets/img/sequoia-whitelogo.png';
import Typography from '@/components/v3/base/Typography';
import CheckFaqIcon from '@/assets/img/check-faq-help-information-mark-question-svgrepo-com.svg';
import Logo from './Logo';
import FlashingButton from '../information-tours/FlashingButton';

export default {
  components: {
    Logo,
    SearchBox,
    FlashingButton,
    Typography,
  },

  props: {
    logo: Boolean,
    isDesktop: Boolean,
    isOpenNav: Boolean,
    isShowIntro: Boolean,
    isCurrentGolive: Boolean,
  },

  data() {
    return {
      linkToken: undefined,
      linkStatus: false,
      profile: false,
      CloseIcon,
      productTypes: Constants.productTypes,
      calendarContents: [
        'Sync your calendar with Hirefly to automate interview scheduling. Hirefly will prompt candidates to book time on your calendar and send them timely reminders in case they forget!',
      ],

      SequoiaLogo,
      CheckFaqIcon,
    };
  },

  computed: {
    authorizedProducts() {
      return (
        localStorage.getItem('authorized_products')?.split(',') ?? [
          this.productTypes.AUTOPILOT,
        ]
      );
    },

    showSwitchToV2() {
      return (
        this.authorizedProducts.length > 1 &&
        this.authorizedProducts.includes(this.productTypes.AUTOPILOT)
      );
    },

    notifications() {
      return this.$store.getters['notifications/listNotifications'];
    },

    notificationsCount() {
      return this.notifications.filter((notification) => !notification.read)
        .length;
    },

    hasNotifications() {
      return this.notificationsCount > 0;
    },

    firstName() {
      return localStorage.getItem('first_name');
    },

    lastName() {
      return localStorage.getItem('last_name');
    },

    fullName() {
      return `Hello, ${this.firstName} ${this.lastName}!`;
    },

    initials() {
      if (!!this.firstName && !!this.lastName) {
        return (
          this.firstName.substring(0, 1) + this.lastName.substring(0, 1)
        ).toUpperCase();
      }
      return '';
    },

    profilePicture() {
      const _profilePicture = localStorage.getItem('profile_picture');

      if (_profilePicture && _profilePicture.includes('https://')) {
        return _profilePicture;
      }
      if (
        _profilePicture !== null &&
        _profilePicture !== '' &&
        _profilePicture !== 'null'
      ) {
        return `data:image/jpeg;base64,${_profilePicture}`;
      }
      return `https://ui-avatars.com/api/?background=ffffff&color=384955&name=${localStorage.getItem(
        'first_name'
      )}+${localStorage.getItem('last_name')}`;
    },

    showSearchBox() {
      const hasProp = 'hideSearchBox' in this.$attrs;
      return !(
        hasProp &&
        (typeof this.$attrs.hideSearchBox === 'string' ||
          this.$attrs.hideSearchBox !== false)
      );
    },

    calendarDisabled() {
      const user = this.$store.getters['user/getUser'];
      if (
        user.calendar_preferences &&
        user.calendar_preferences.calendars_avail.length === 0
      ) {
        return true;
      }
      return false;
    },

    showSequoiaLogo() {
      const userEmail = localStorage.getItem('email');
      return /@*sequoiacap.com\s*$/.test(userEmail);
    },
  },

  created() {
    this.$store.dispatch('notifications/LOAD');
  },

  mounted() {
    if (
      (this.authorizedProducts.includes(this.productTypes.NAVIGATOR) ||
        this.authorizedProducts.includes(this.productTypes.FAST_TRACK)) &&
      'is_autopilot' in this.$route.query
    ) {
      const isAutopilot = this.$route.query.is_autopilot;
      if (isAutopilot.toString() === 'false') {
        this.onRedirectToV2();
      }
    }
  },

  methods: {
    onRedirectToV2() {
      Cookies.set('site_version', 'v2');
      Sentry.setContext('site_version', {
        version: 'v2',
      });
      window.open(`${window.location.origin}/panel`, '_self');
    },

    showCalendar() {
      this.profile = true;
    },

    openCalendar() {
      if (!this.calendarDisabled) {
        this.$router.push('/calendar');
      }
    },

    signOut() {
      consola.info('In sign out method');
      axios.defaults.headers.delete['Content-Type'] =
        'application/x-www-form-urlencoded';
      const session = localStorage.getItem('session');
      axios.defaults.headers.delete['X-Parse-Session-Token'] = session;

      axios
        .delete(`${Config.env.API_URL}sessions`)
        .then(() => {
          this.$store.dispatch('auth/logout');
          window.location.reload();
        })
        .catch((error) => {
          consola.error(error);
          this.$store.dispatch('auth/logout');
          window.location.reload();
        });
    },

    openNotification(notification) {
      // notification.project_id = '60249b7d91571e2b7f0a0b21';
      // notification.cand_id = '60263008ea916a0310863648';
      this.$router.push({
        name: 'Panel',
        query: {
          project: notification.project_id,
          candidate: notification.cand_id,
        },
      });

      this.$store.dispatch('notifications/SET_READ', { notification });
    },

    async onSuccess(token) {
      // Pass token to your backend (Step 3)
      console.log(`Success token:${token}`);
      this.linkStatus = true;

      const response = await post(`${Config.env.API_URL}link/update`, {
        public_token: token,
      });

      console.log(response);
      this.$store.dispatch('user/LOAD', { force: true });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/styles/variables.scss';

.hamburger {
  &.opened {
    transform: translateX(241px);
  }
}

.header {
  height: fit-content !important;
  z-index: 20 !important;
  &.is-intro {
    z-index: 0 !important;
    &::v-deep .v-toolbar__content {
      z-index: 5;
    }
  }

  &::v-deep .v-toolbar__content {
    height: 61px !important;
  }
  &::v-deep .v-toolbar__extension {
    height: 59px !important;
    padding: 0;
  }
  &::v-deep .v-menu__content {
    box-shadow: -2px 3px 9px 4px rgba(0, 0, 0, 0.2);
  }
}

.notification-block__title {
  padding: 16px 0;
  border-bottom: 1px solid #e6e7eb;
  font-size: 16px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: 0.5px;
  color: $gray-dark;
}

.notification-list {
  padding: 0;

  .notification-list-item {
    &:not(:last-child) {
      border-bottom: 3px solid rgba(216, 216, 216, 0.56);
    }
  }
}

.logo {
  height: 61px !important;
  padding: 0 0 0 50px !important;
  background-color: $primary !important;
  z-index: 100 !important;
  flex: 0 0 auto !important;
}
.avatar {
  color: black !important;
}
.notifications {
  max-height: 445px !important;
}

.status-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background-color: #d8d8d8;
}

.status-dot.done {
  background-color: #37b797;
}
.is-disabled {
  opacity: 0.6;
  cursor: unset;
}
.info-position {
  position: absolute;
  right: 77%;
  top: 14px;
}
.profile-position {
  position: absolute;
  right: 6px;
  top: 37px;
}
.close-icon-btn {
  width: 30px;
  height: 30px;
  border-radius: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  img {
    width: 24px;
  }
}
.overlay {
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.15);
  z-index: 1;
}
.z-10 {
  z-index: 10;
}
.sequoia-logo {
  width: 128px;
  img {
    width: 128px;
  }
}
::v-deep {
  .v-btn--disabled {
    img {
      opacity: 0.3;
    }
  }
}
</style>
